import { Contract, Utxo } from '@atomicfinance/bitcoin-dlc-provider';
import { decodeRawTransaction } from '@liquality/bitcoin-utils';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import crypto from 'crypto';
import { utils } from 'eciesjs';
import config from '../../config/config';
import { persistBet } from '../../hooks/usePersistBet';
import { client as batchClient } from '../../services/batchChainClient';
import { createClient, getClient } from '../../services/chainClient';
import {
  getTowerService,
  hasTowerCredentials,
} from '../../services/towerService';
import { getTwitterInfoById } from '../../services/twitter';
import { PROPER_RESOLUTION_DATES } from '../../utils/Election';
import { RootState } from '../reducers';

export enum BetState {
  Pending = 'PENDING',
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  Accept = 'ACCEPT',
  Offer = 'OFFER',
  Sign = 'SIGN',
  Withdrawable = 'WITHDRAWABLE',
  Cancelled = 'CANCELLED',
}

export class Tx {
  constructor(
    readonly blockHash: string,
    readonly blockNumber: number,
    readonly confirmations: number,
    readonly fee: number,
    readonly feePrice: number,
    readonly hash: string,
    readonly value: number
  ) {}
}

// type Path = 'open' | 'localToCet' | 'localToRemote' | 'remoteToCet' | 'remoteToLocal'

export class BetClose {
  constructor(
    // readonly path: Path = 'open',
    readonly path: string = 'open',
    readonly withdrawn: boolean = false,
    readonly withdrawable: boolean = false,
    readonly outcomeIndex?: number,
    readonly signature?: string,
    readonly isWinner?: boolean,
    readonly txHex?: string,
    readonly tx?: Tx
  ) {}
}

export const OFFER_STAGES = [BetState.Offer, BetState.Accept, BetState.Sign];
export const ACTIVE_STAGES = [BetState.Withdrawable, BetState.Active];

export const doesBetHaveAction = (bet) => {
  switch (bet.stage) {
    case 'OFFER':
      return !!bet.towerInfo?.accept;
    case 'ACCEPT':
      return !!bet.towerInfo?.sign;
    case 'WITHDRAWABLE':
      return true;
    default:
      return false;
  }
};

interface BetDetails {
  asset: string;
  betAmount: number;
  betSize: number;
  eventType: string;
  eventValue: number;
  prediction: string;
  resolutionDate: Date;
}

interface Bet {
  betDetails: BetDetails;
  contract: any;
  contractId: string;
  imported: boolean;
  stage: BetState;
  towerInfo?: any;
  closeInfo?: BetClose;
  withdrawable?: boolean;
}

const hydrateBets = createAsyncThunk(
  'bets/hydrate',
  async (_, { dispatch, getState }) => {
    const {
      wallet: { mnemonic },
    } = getState() as RootState;

    if (!mnemonic) throw new Error('mnemonic not set');

    const betIdsRaw = localStorage.getItem('betIds');
    const betIds = betIdsRaw ? betIdsRaw.split(',') : [];

    const mnemonicKey = crypto.createHash('sha256').update(mnemonic).digest();
    const bets: Bet[] = [];

    betIds.forEach((id) => {
      const encryptedBet = localStorage.getItem(id);
      if (!encryptedBet) return;

      try {
        const bet = JSON.parse(
          utils
            .aesDecrypt(mnemonicKey, Buffer.from(encryptedBet, 'base64'))
            .toString()
        );

        bets.push(bet);
      } catch (e) {
        console.error(e);
      }
    });

    // dispatch(importBets(bets));

    return bets.reduce((map, bet) => {
      map[bet.contractId] = bet;
      return map;
    }, {});
  }
);

const importBet = createAsyncThunk(
  'bets/importBets',
  async (id: string, { getState, dispatch }) => {
    const {
      bets: { [id]: bet },
      wallet: { mnemonic },
    } = getState() as RootState;

    const bitcoin = createClient(mnemonic);

    const contract: Contract = Contract.fromJSON(bet.contract);
    const hasDlc: boolean = bitcoin.client.finance.dlc.hasDlc(contract.id);

    if (!hasDlc) {
      console.log(contract);

      try {
        await bitcoin.client.finance.dlc.importContract(contract);

        dispatch(
          updateBet({
            contractId: contract.id,
            updates: {
              imported: true,
            },
          })
        );
      } catch (e) {
        console.error(e);
      }
    }
  }
);

const performBetAction = createAsyncThunk(
  'bets/performBetAction',
  async (id: string, { getState, dispatch }) => {
    const {
      bets: { [id]: bet },
      wallet: { mnemonic },
    } = getState() as RootState;

    const bitcoin = createClient(mnemonic);
    if (!bet.imported) await dispatch(importBet(id));

    switch (bet.stage) {
      case BetState.Offer: {
        const acceptMessage = bet.towerInfo?.accept;
        console.log(acceptMessage);
        if (acceptMessage) {
          try {
            const signMessage = await bitcoin.signContract(acceptMessage);
            console.log('signMessage', signMessage);

            dispatch(
              updateBet({
                contractId: bet.contractId,
                updates: {
                  stage: 'SIGN',
                },
              })
            );
            await getTowerService().postSign(
              signMessage,
              acceptMessage.remotePartyInputs.fundPublicKey,
              bet.contract.startingIndex
            );

            persistBet(signMessage.contractId, null, 'SIGN');
          } catch (e) {
            console.error(e);
          }
        }
        break;
      }
      case BetState.Accept: {
        const signMessage = bet.towerInfo?.sign;
        if (signMessage) {
          try {
            const tx = await bitcoin.finalizeContract(signMessage);
            console.log('tx', tx);

            dispatch(
              updateBet({
                contractId: bet.contractId,
                updates: {
                  stage: 'ACTIVE',
                },
              })
            );

            persistBet(signMessage.contractId, null, 'ACTIVE');
          } catch (e) {
            console.error(e);
          }
        }
      }
    }
  }
);

interface IdAndUtxos {
  id: string;
  utxos: Utxo[];
}

const fetchBetState = createAsyncThunk(
  'bets/fetchState',
  async ({ id, utxos }: IdAndUtxos, { getState, dispatch }) => {
    const {
      bets: { [id]: bet },
      wallet: { mnemonic },
    } = getState() as RootState;
    if (!bet) throw new Error('Bet does not exist.');

    console.log(bet);

    const { contractId, contract } = bet;

    let _utxos: Utxo[] = [];
    if (utxos.length === 0) {
      _utxos = await getFundedUtxos({ [contractId]: bet }, mnemonic);
    } else {
      _utxos = utxos;
    }

    const tower = hasTowerCredentials()
      ? getTowerService()
      : await getTowerService(createClient(mnemonic)).initialize();
    const localInputsFunded = checkUtxosFunded(
      _utxos,
      contract.localPartyInputs.utxos
    );

    // add twitterInfo

    try {
      if (!contract.isLocalParty) {
        const twitterId = await tower.findTwitterId(contractId);
        const twitterInfo = await getTwitterInfoById(twitterId);

        dispatch(
          updateBet({
            contractId,
            updates: {
              twitterInfo,
            },
          })
        );
      }
    } catch (e) {
      console.error(e);
    }

    switch (bet.stage) {
      case 'OFFER': {
        try {
          const accept = await tower.getAccept(
            contractId,
            contract.startingIndex
          );
          const remoteInputsFunded = checkUtxosFunded(
            _utxos,
            accept.remotePartyInputs.utxos
          );
          const inputsFunded = localInputsFunded && remoteInputsFunded;

          dispatch(
            updateBet({
              contractId,
              updates: {
                towerInfo: { accept },
                stage: inputsFunded ? 'OFFER' : 'CANCELLED',
                withdrawable:
                  contract.isLocalParty &&
                  !remoteInputsFunded &&
                  localInputsFunded,
              },
            })
          );
        } catch (e) {
          if (!localInputsFunded) {
            dispatch(
              updateBet({
                contractId,
                updates: {
                  stage: 'CANCELLED',
                },
              })
            );
          }
          console.error(e);
        }
        break;
      }
      case 'ACCEPT': {
        const remoteInputsFunded = checkUtxosFunded(
          _utxos,
          contract.remotePartyInputs.utxos
        );
        const inputsFunded = localInputsFunded && remoteInputsFunded;
        try {
          const sign = await tower.getSign(
            contractId,
            contract.localPartyInputs.fundPublicKey,
            contract.startingIndex
          );

          dispatch(
            updateBet({
              contractId,
              updates: {
                towerInfo: { sign },
                stage: inputsFunded ? 'ACCEPT' : 'CANCELLED',
                withdrawable:
                  !contract.isLocalParty &&
                  remoteInputsFunded &&
                  !localInputsFunded,
              },
            })
          );
        } catch (e) {
          if (!remoteInputsFunded) {
            dispatch(
              updateBet({
                contractId,
                updates: {
                  stage: remoteInputsFunded ? 'ACCEPT' : 'CANCELLED',
                },
              })
            );
          }
        }

        break;
      }
      case 'SIGN': {
        try {
          await getClient().getMethod('getTransaction')(contract.fundTxId);

          await dispatch(importBet(contractId));

          persistBet(contractId, null, 'ACTIVE');

          dispatch(
            updateBet({
              contractId: contractId,
              updates: {
                stage: 'ACTIVE',
              },
            })
          );
        } catch (e) {
          const remoteInputsFunded = checkUtxosFunded(
            _utxos,
            contract.remotePartyInputs.utxos
          );
          const inputsFunded = localInputsFunded && remoteInputsFunded;
          dispatch(
            updateBet({
              contractId,
              updates: {
                stage: inputsFunded ? 'SIGN' : 'CANCELLED',
                withdrawable:
                  contract.isLocalParty &&
                  !remoteInputsFunded &&
                  localInputsFunded,
              },
            })
          );
        }

        break;
      }
      case 'WITHDRAWABLE':
      case 'ACTIVE': {
        const { resolutionDate } = bet.betDetails;
        const contract = bet.contract;
        if (new Date(resolutionDate) < new Date()) {
          let value = '';
          let signature = '';
          try {
            const oracleData = (
              await axios.get(
                `${config.oracle.url}/asset/election/signature/${resolutionDate}?eventType=election`
              )
            ).data;
            value = oracleData.value;
            signature = oracleData.signature;
          } catch (e) {
            const resolutionDateNotInProperResolutionDates = !PROPER_RESOLUTION_DATES.find(
              (item) => {
                return item.getTime() === new Date(resolutionDate).getTime();
              }
            );
            // check if resolution date in list from Election
            if (resolutionDateNotInProperResolutionDates) {
              value = 'republican';
              signature =
                '1d4ac5d31c46bba266ac841cec8713b4ba67b1a3731b24dd8b655497f11b97ac';
            }
          }

          await dispatch(importBet(contractId));

          // console.log(value, signature, eventValue);

          const outcomeIndex = contract.outcomes.findIndex(
            (outcome) => outcome.message === value
          );

          const isWinner = value === '' ? false : (contract.isLocalParty ? 0 : 1) === outcomeIndex;
          const network = await getClient().getMethod('getConnectedNetwork')();

          const allCets = [...contract.localCetsHex, ...contract.remoteCetsHex];
          console.log(allCets);
          const finalAddress = contract.isLocalParty
            ? contract.localPartyInputs.finalAddress
            : contract.remotePartyInputs.finalAddress;

          const betClose =
            outcomeIndex !== -1 &&
            (await getBetClose(
              allCets,
              network,
              finalAddress,
              contract.isLocalParty,
              isWinner
            )); // TODO: don't getBetClose if currently in DLC (easy check)
          const newStage =
            value === ''
              ? 'ACTIVE'
              : isWinner && betClose && betClose.withdrawable
              ? 'WITHDRAWABLE'
              : 'CLOSED';
          console.log('betClose', betClose);
          dispatch(
            updateBet({
              contractId: bet.contractId,
              updates: {
                stage: newStage,
                closeInfo: { ...betClose, signature, outcomeIndex },
              },
            })
          );

          persistBet(bet.contractId, null, newStage);
        }
      }
    }
  }
);

export const getFundedUtxos = async (bets: any, mnemonic: string) => {
  const tower = hasTowerCredentials()
    ? getTowerService()
    : await getTowerService(createClient(mnemonic)).initialize();

  let txos: Utxo[] = [];
  for (const betEntries of Object.entries(bets)) {
    const bet: any = betEntries[1];

    const contract = bet.contract;
    const { localPartyInputs, remotePartyInputs, id, startingIndex } = contract;

    localPartyInputs.utxos.forEach((txo) => txos.push(txo));
    remotePartyInputs?.utxos.forEach((txo) => txos.push(txo));

    try {
      const accept = await tower.getAccept(id, startingIndex);

      accept.remotePartyInputs.utxos.forEach((txo) => txos.push(txo));
    } catch (e) {}
  }

  txos.filter(
    (txo, index, txoArray) =>
      txoArray.findIndex((_txo) => _txo.txid === txo.txid) === index
  );
  const unfiltedAddresses = txos.map((txo) => txo.address);
  const addresses = unfiltedAddresses.filter(
    (addr, index) => unfiltedAddresses.indexOf(addr) === index
  );
  const utxos = await batchClient.client.getMethod('getUnspentTransactions')(
    addresses
  );

  return utxos;
};

const checkUtxosFunded = (fundedUtxos, utxos) => {
  let funded = true;
  utxos.forEach((utxo) => {
    if (!fundedUtxos.some((_utxo) => _utxo.txid === utxo.txid)) {
      funded = false;
    }
  });
  return funded;
};

const fetchAllBetStates = createAsyncThunk(
  'bets/fetchAllState',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const {
      bets,
      wallet: { mnemonic },
    } = state;
    const utxos = await getFundedUtxos(bets, mnemonic);

    await Promise.all(
      Object.values(bets).map((bet: any) =>
        dispatch(fetchBetState({ id: bet.contractId, utxos }))
      )
    );
  }
);

const paths = [
  'open',
  'localToCet',
  'localToRemote',
  'remoteToLocal',
  'remoteToCet',
];

const getBetClose = async (
  allCets,
  network,
  finalAddress,
  isLocalParty,
  winner
): Promise<BetClose> => {
  try {
    console.log(network);
    const cetTx = decodeRawTransaction(allCets[0], network);
    console.log(cetTx);
    const cetTxFinal = await getClient().getMethod('getTransaction')(
      cetTx.txid
    );
    console.log(finalAddress);

    if (cetTxFinal.hash === cetTx.txid) {
      const path = paths[paths.length - allCets.length];

      if (path === 'localToCet' || path === 'remoteToCet') {
        let utxos = await getClient().getMethod('getUnspentTransactions')([
          cetTx.vout[0].scriptPubKey.addresses[0],
        ]);

        if (utxos.length === 0) {
          return getBetWithdraw(
            path === 'localToCet' ? 'localToLocal' : 'remoteToRemote',
            finalAddress,
            isLocalParty,
            winner
          );
        } else {
          return getBetWithdraw(
            path,
            finalAddress,
            isLocalParty,
            winner,
            allCets[0],
            cetTxFinal
          );
        }
      } else {
        return getBetWithdraw(
          path,
          finalAddress,
          isLocalParty,
          winner,
          allCets[0],
          cetTxFinal
        );
      }
    }
  } catch (e) {
    console.log(e);
    if (allCets.length > 1) {
      return getBetClose(
        allCets.slice(1),
        network,
        finalAddress,
        isLocalParty,
        winner
      );
    }
  }
  console.log('winner', winner);
  return {
    path: 'open',
    withdrawn: false,
    withdrawable: winner,
    isWinner: winner,
  };
};

const getBetWithdraw = async (
  path,
  finalAddress,
  isLocalParty,
  winner,
  txHex?,
  tx?
): Promise<BetClose> => {
  const withdrawablePaths = [
    'localToLocal',
    'localToRemote',
    'remoteToLocal',
    'remoteToRemote',
  ];
  let withdrawn = false;
  let withdrawable = false;
  if (withdrawablePaths.includes(path)) {
    const utxos = await getClient().getMethod('getUnspentTransactions')([
      finalAddress,
    ]);

    if (utxos.length === 0) {
      withdrawn = true;
    } else if (
      isLocalParty &&
      (path === 'localToLocal' || path === 'remoteToLocal')
    ) {
      withdrawable = true;
    } else if (
      !isLocalParty &&
      (path === 'localToRemote' || path === 'remoteToRemote')
    ) {
      withdrawable = true;
    }
  } else if (isLocalParty && path === 'localToCet' && winner) {
    withdrawable = true;
  } else if (!isLocalParty && path === 'remoteToCet' && winner) {
    withdrawable = true;
  }
  return { path, withdrawn, withdrawable, txHex, tx, isWinner: winner };
};

interface BetSliceState {
  [contractId: string]: Bet;
}

type UpdateBetActionPayload = {
  contractId: string;
  updates: any;
};

const betsSlice = createSlice({
  name: 'bets',
  initialState: {} as BetSliceState,
  reducers: {
    updateBet(state, action: PayloadAction<UpdateBetActionPayload>) {
      Object.assign(state[action.payload.contractId], action.payload.updates);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateBets.fulfilled, (state, action) => {
      console.log(action.payload);
      Object.assign(state, action.payload);
    });
  },
});

export const { updateBet } = betsSlice.actions;
export {
  hydrateBets,
  fetchBetState,
  fetchAllBetStates,
  importBet,
  performBetAction,
};
export default betsSlice.reducer;
